import { isAxiosError } from 'axios';

import { CreditApiGetCreditScheduleRequest, CreditTransactionRecord } from '@videoblocks/subscription-service-client';
import { LoggerFactory } from '@videoblocks/typescript-logger';

import { BadRequestError } from './BadRequestError';
import { ProcessingError } from './ProcessingError';
import { UnexpectedError } from './UnexpectedError';
import { creditApi } from './subscriptionServiceConfig';

const logger = LoggerFactory.create('Credit Api');

const getBalance = async (accountCode: string, userId?: number): Promise<number> => {
    const params = { accountCode, userId };
    try {
        const response = await creditApi.getBalance(params);
        return response.data.balance ?? 0;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn({ error, params }, 'Bad request error while fetching account balance');
                throw new BadRequestError();
            } else if ((error.response?.status || 0) >= 500) {
                logger.warn({ error, params }, 'Processing error while fetching account balance');
                throw new ProcessingError(
                    'There was an error while fetching your account balance.\n Please try again later.',
                );
            }
        }
        logger.error({ error, params }, 'An unexpected error occurred while fetching account balance');
        throw new UnexpectedError();
    }
};

const getMonthlyCreditAllowance = async (organizationId: number): Promise<number | null> => {
    try {
        const request: CreditApiGetCreditScheduleRequest = {
            accountCode: `org_${organizationId}`,
        };
        const response = await creditApi.getCreditSchedule(request);
        return response.data.monthlyAmount ?? 0;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.error(
                    { error, organizationId },
                    'Bad request error while fetching credit schedule for organization',
                );
                throw new BadRequestError();
            } else if (error.response?.status === 404) {
                logger.info({ organizationId }, 'No credit schedule found');
                return null;
            } else if ((error.response?.status || 0) >= 500) {
                logger.error(
                    { error, organizationId },
                    'Processing error while fetching credit schedule for organization',
                );
                throw new ProcessingError('There was an error while fetching an organization credit schedule.');
            }
        }
    }
    logger.error({ organizationId }, 'An unexpected error occurred while fetching organization credit schedule)');
    throw new UnexpectedError();
};

const getTransactionHistory = async (accountCode: string, userId?: number): Promise<CreditTransactionRecord[]> => {
    const params = { accountCode, userId };
    try {
        const response = await creditApi.getTransactionHistory(params);
        return response.data;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn({ error, params }, 'Bad request error while fetching transaction history');
                throw new BadRequestError();
            } else if ((error.response?.status || 0) >= 500) {
                logger.warn({ error, params }, 'Processing error while fetching transaction history');
                throw new ProcessingError(
                    'There was an error while fetching your transaction history.\n Please try again later.',
                );
            }
        }
        logger.error({ error, params }, 'An unexpected error occurred while fetching transaction history');
        throw new UnexpectedError();
    }
};

const postTransaction = async (
    accountCode: string,
    creditTransactionRecord: Omit<CreditTransactionRecord, 'accountCode'>,
): Promise<number> => {
    const params = {
        accountCode,
        creditTransactionRecord: { ...creditTransactionRecord, accountCode },
    };
    try {
        const response = await creditApi.postTransaction(params);
        return response.data.balance;
    } catch (error) {
        if (isAxiosError(error)) {
            if (error.response?.status === 400) {
                logger.warn({ error, params }, 'Bad request error while posting credit transaction');
                throw new BadRequestError();
            } else if ((error.response?.status || 0) >= 500) {
                logger.warn({ error, params }, 'Processing error while posting credit transaction');
                throw new ProcessingError(
                    'There was an error while processing your transaction.\n Please try again later.',
                );
            }
        }
        logger.error({ error, params }, 'An unexpected error occurred while posting credit transaction');
        throw new UnexpectedError();
    }
};

export const CreditApi = {
    getBalance,
    getMonthlyCreditAllowance,
    getTransactionHistory,
    postTransaction,
};
